/**
 * DataHealth.jsx — admin "data hygiene" dashboard.
 *
 * Surfaces five common data-quality issues in one place and offers a
 * one-click cleanup where it's safe to do so. Non-fixable checks
 * (duplicate SKU codes, duplicate location codes) expose the groups so
 * an operator can manually reconcile.
 *
 * Zero fetches on the list/detail endpoints until the operator expands a
 * check — keeps the default render snappy on a large tenant.
 */
import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import {
  getDataHealthSummary, getDataHealthCheck, cleanupDataHealthCheck,
} from '../lib/api';
import { Button } from '../components/ui/button';
import {
  Heartbeat, ArrowClockwise, Broom, Warning, CheckCircle,
  CaretDown, CaretRight, Info,
} from '@phosphor-icons/react';

const SEVERITY_STYLES = {
  high: 'border-rose-300 bg-rose-50',
  medium: 'border-amber-300 bg-amber-50',
  low: 'border-sky-300 bg-sky-50',
  ok: 'border-emerald-300 bg-emerald-50',
};
const SEVERITY_BADGE = {
  high: 'bg-rose-600 text-white',
  medium: 'bg-amber-600 text-white',
  low: 'bg-sky-600 text-white',
  ok: 'bg-emerald-600 text-white',
};

export default function DataHealth() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // key of expanded check
  const [detail, setDetail] = useState({}); // key → detail payload
  const [busy, setBusy] = useState(null); // key currently being cleaned up

  const load = async () => {
    try {
      setLoading(true);
      const res = await getDataHealthSummary();
      setSummary(res.data);
    } catch (e) {
      toast.error('Failed to load data health summary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const toggleExpand = async (key) => {
    if (expanded === key) { setExpanded(null); return; }
    setExpanded(key);
    if (!detail[key]) {
      try {
        const res = await getDataHealthCheck(key);
        setDetail((d) => ({ ...d, [key]: res.data }));
      } catch (e) {
        toast.error(`Failed to load ${key} details`);
      }
    }
  };

  const runCleanup = async (key, label) => {
    if (!window.confirm(
      `Permanently delete all rows flagged by "${label}"? This can't be undone.`
    )) return;
    try {
      setBusy(key);
      const res = await cleanupDataHealthCheck(key);
      const n = res.data?.deleted || 0;
      toast.success(`Deleted ${n} row${n === 1 ? '' : 's'}`, {
        description: `"${label}" is now clean.`,
      });
      // Refresh both summary + detail
      setDetail((d) => { const { [key]: _, ...rest } = d; return rest; });
      setExpanded(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cleanup failed');
    } finally {
      setBusy(null);
    }
  };

  const checks = summary?.checks || [];
  const totalIssues = summary?.total_issues || 0;

  return (
    <Layout>
      <div data-testid="data-health-page" className="p-6 max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="p-2.5 bg-brand/10 rounded">
              <Heartbeat size={28} weight="duotone" className="text-brand" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Data Health</h1>
              <p className="text-muted-foreground">
                Find &amp; fix common data-hygiene problems in your warehouse data.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="data-health-refresh-btn"
          >
            <ArrowClockwise size={16} weight="bold" className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Top summary banner */}
        <div
          className={`border-2 rounded p-4 mb-6 ${
            totalIssues === 0 ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'
          }`}
          data-testid="data-health-summary-banner"
        >
          {loading ? (
            <div className="text-sm text-muted-foreground">Scanning…</div>
          ) : totalIssues === 0 ? (
            <div className="flex items-center gap-2 text-emerald-800">
              <CheckCircle size={20} weight="bold" />
              <div>
                <div className="font-bold">All clean.</div>
                <div className="text-sm">No data-hygiene issues detected. Nice.</div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-amber-900">
              <Warning size={20} weight="bold" />
              <div>
                <div className="font-bold text-lg" data-testid="data-health-total-issues">
                  {totalIssues} issue{totalIssues === 1 ? '' : 's'} found across {checks.filter(c => c.count > 0).length} check{checks.filter(c => c.count > 0).length === 1 ? '' : 's'}
                </div>
                <div className="text-sm">
                  Expand each check below to review the affected rows, then run the cleanup if it's safe to delete.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Checks */}
        <div className="space-y-3">
          {loading && !summary ? (
            [1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-muted animate-pulse rounded" />
            ))
          ) : (
            checks.map((c) => {
              const isExp = expanded === c.key;
              const d = detail[c.key];
              return (
                <div
                  key={c.key}
                  data-testid={`data-health-check-${c.key}`}
                  className={`border rounded ${SEVERITY_STYLES[c.severity] || 'border-border bg-card'}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleExpand(c.key)}
                    className="w-full text-left p-4 flex items-center gap-3 hover:bg-black/[0.03] transition"
                    data-testid={`data-health-expand-${c.key}`}
                  >
                    {isExp ? <CaretDown size={18} weight="bold" /> : <CaretRight size={18} weight="bold" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold">{c.label}</span>
                        <span
                          className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${SEVERITY_BADGE[c.severity] || 'bg-muted text-foreground'}`}
                          data-testid={`data-health-severity-${c.key}`}
                        >
                          {c.severity}
                        </span>
                        {!c.fixable && (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-slate-700 text-white flex items-center gap-1">
                            <Info size={10} weight="bold" /> manual
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{c.description}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div
                        className={`text-2xl font-mono font-bold ${c.count === 0 ? 'text-emerald-700' : 'text-foreground'}`}
                        data-testid={`data-health-count-${c.key}`}
                      >
                        {c.count}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">issues</div>
                    </div>
                  </button>

                  {isExp && (
                    <div className="border-t border-border/50 p-4 bg-white/60" data-testid={`data-health-detail-${c.key}`}>
                      {!d ? (
                        <div className="text-sm text-muted-foreground">Loading…</div>
                      ) : c.count === 0 ? (
                        <div className="text-sm text-emerald-700 font-medium">Clean — nothing to show.</div>
                      ) : (
                        <>
                          {/* Groups-style (duplicates) */}
                          {d.groups ? (
                            <div className="space-y-3">
                              <div className="text-xs text-muted-foreground">
                                Showing first {d.groups.length} duplicate group{d.groups.length === 1 ? '' : 's'}.
                              </div>
                              {d.groups.map((g, i) => (
                                <div key={i} className="border border-border rounded p-3 bg-white">
                                  <div className="font-mono font-bold text-sm mb-2">
                                    {g.sku_code || g.location_code} · <span className="text-muted-foreground">{g.count} copies</span>
                                  </div>
                                  <ul className="text-xs text-muted-foreground space-y-1">
                                    {(g.skus || g.locations || []).map((row, j) => (
                                      <li key={j} className="font-mono">
                                        <span className="inline-block w-[120px] truncate align-top text-slate-400">{row.id}</span>
                                        {row.name || row.zone || ''}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          ) : (
                            /* Sample rows */
                            <div className="space-y-2">
                              <div className="text-xs text-muted-foreground">
                                Showing {d.sample?.length || 0} of {d.count} affected row{d.count === 1 ? '' : 's'}.
                              </div>
                              <div className="border border-border rounded bg-white font-mono text-xs overflow-x-auto">
                                <table className="w-full">
                                  <thead>
                                    <tr className="border-b border-border bg-muted">
                                      {Object.keys(d.sample?.[0] || {}).map((k) => (
                                        <th key={k} className="px-2 py-1.5 text-left text-[10px] uppercase font-bold tracking-wider">{k}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(d.sample || []).map((row, i) => (
                                      <tr key={i} className="border-b last:border-b-0 border-border/60">
                                        {Object.entries(row).map(([k, v]) => (
                                          <td key={k} className="px-2 py-1 truncate max-w-[200px]">
                                            {v === null || v === undefined ? <span className="text-slate-300">—</span> : String(v)}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* Cleanup CTA */}
                          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                            <div className="text-xs text-muted-foreground">
                              {c.fixable
                                ? 'Rows are safe to delete — they carry no recoverable info.'
                                : 'Manual review required. We don\'t auto-merge because only you know which copy to keep.'}
                            </div>
                            {c.fixable && (
                              <Button
                                onClick={() => runCleanup(c.key, c.label)}
                                disabled={busy === c.key || c.count === 0}
                                className="bg-rose-600 hover:bg-rose-700 text-white"
                                data-testid={`data-health-cleanup-${c.key}`}
                              >
                                <Broom size={14} weight="bold" className="mr-2" />
                                {busy === c.key ? 'Cleaning…' : `Delete all ${c.count}`}
                              </Button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </Layout>
  );
}
